import React, { useEffect, useMemo } from 'react'
import styled from 'styled-components'
import { Text } from 'rebass'
import { RouteComponentProps } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { provider } from 'web3-core'

import { getContract } from '../../utils/bakery/erc20'
import { Card } from '../../components/bakery/styleds'
import { ButtonPrimary } from '../../components/Button'
import Harvest from './components/Harvest'
import StakeNFT from './components/StakeNFT'

// hook
import { useWalletModalToggle } from '../../state/application/hooks'
import useFarm from '../../hooks/bakery/useFarm'
import { useActiveWeb3React } from '../../hooks'

import { MINTTOKEN } from '../../constants/bakery'

import TopStakers from './components/TopStakers'

const BodyHead = styled.div`
  color: ${({ theme }) => theme.bsText3};
  text-align: center;
  margin-bottom: 50px;
`
const TitleText = styled(Text)`
  font-family: Baloo Da;
  color: ${({ theme }) => theme.bsText4};
`
const CardWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 26px;
  width: 100%;

  ${({ theme }) => theme.mediaWidth.upToMedium`
    flex-direction: column;
  `}
`
const EarnCard = styled(Card)`
  position: relative;
  background-repeat: no-repeat;
  background-position: top right;
  color: ${({ theme }) => theme.bsText3};
  text-align: center;
  padding: 24px;
  &:first-child {
    margin-right: 32px;
  }

  ${({ theme }) => theme.mediaWidth.upToMedium`
    &:first-child {
      margin-right: initial;
      margin-bottom: 32px;
    }
  `}
`
const CurrencyIcon = styled.div`
  margin: 8px 0 16px;
`

const RewardsText = styled.div`
  color: ${({ theme }) => theme.bsText3};
  text-align: center;
  margin-top: 40px;
  padding: 0 24px;
`
const LoginBtn = styled(ButtonPrimary)`
  margin: 54px auto 0;
  width: initial;
  padding: 8px 16px;
`
const LoginBtnWrapper = styled.div``

export default function BreadNFT({
  match: {
    params: { earn, bakeryId }
  },
  history
}: RouteComponentProps<{ earn?: string; bakeryId?: string }>) {
  const { t } = useTranslation()
  const { active, account, library } = useActiveWeb3React()
  const toggleWalletModal = useWalletModalToggle()
  const ethereum = library?.provider

  const {
    pid,
    lpToken,
    lpTokenAddress,
    earnToken,
    name,
    icon,
    activityTime,
    stakeIcon,
    otherName
  } = useFarm(bakeryId, earn) || {
    pid: 0,
    lpToken: '',
    lpTokenAddress: '',
    tokenAddress: '',
    earnToken: '',
    name: '',
    icon: '',
    activityTime: '',
    stakeIcon: '',
    otherName: ''
  }

  useEffect(() => {
    if (
      !pid ||
      !earn ||
      [
        MINTTOKEN.TKONFTSTAKE,
        MINTTOKEN.BMONCNFTSTAKE,
        MINTTOKEN.TOYNFTSTAKE,
        MINTTOKEN.BMMNFTSTAKE
      ].indexOf(earn) < 0
    ) {
      history.push(`/bakery`)
    }
  }, [earn, pid, history])

  const lpTokenName = useMemo(() => {
    return lpToken.toUpperCase()
  }, [lpToken])

  const earnTokenName = useMemo(() => {
    return earnToken.toUpperCase()
  }, [earnToken])

  const lpContract = useMemo(() => {
    return getContract(ethereum as provider, lpTokenAddress)
  }, [ethereum, lpTokenAddress])

  if (!pid) {
    return null
  }
  return (
    <>
      {!(active && account) ? (
        <LoginBtnWrapper>
          <LoginBtn onClick={toggleWalletModal}>{t('Connect to a wallet')}</LoginBtn>
        </LoginBtnWrapper>
      ) : (
        <>
          <BodyHead>
            <img
              src={'/images/bakery/icon_' + (/\.png/gi.test(icon as string) ? icon : icon + '.svg')}
              style={{ height: 82 }}
              alt="logo"
            />
            <>
              <TitleText fontSize={32} fontWeight={700}>
                {name}
              </TitleText>
              <Text fontSize={14} fontWeight={500}>
                {activityTime ? activityTime : `Deposit ${lpTokenName} Tokens and earn ${otherName || earnTokenName}`}
              </Text>
            </>
          </BodyHead>
          <CardWrapper>
            <EarnCard
              style={{ backgroundImage: 'url(/images/bakery/icon_' + earnTokenName.toLowerCase() + '_light.svg)' }}
            >
              <CurrencyIcon>
                <img
                  src={'/images/bakery/icon_' + earnTokenName.toLowerCase() + '.svg'}
                  style={{ height: 68 }}
                  alt="logo"
                />
              </CurrencyIcon>
              <Harvest
                name={name}
                earnTokenName={earnTokenName}
                lpTokenAddress={lpTokenAddress}
                otherName={otherName}
              />
            </EarnCard>
            <EarnCard
              style={{
                backgroundImage:
                  'url(/images/bakery/icon_' +
                  (stakeIcon
                    ? stakeIcon + '_light.svg)'
                    : /\.png/gi.test(icon as string)
                    ? (icon as string).replace('.png', '_light.png)')
                    : icon + '_light.svg)')
              }}
            >
              <CurrencyIcon>
                <img
                  src={
                    '/images/bakery/icon_' +
                    (stakeIcon ? stakeIcon + '.svg' : /\.png/gi.test(icon as string) ? icon : icon + '.svg')
                  }
                  style={{ height: 68 }}
                  alt="logo"
                />
              </CurrencyIcon>
              <StakeNFT
                name={name}
                lpContract={lpContract}
                lpTokenAddress={lpTokenAddress}
                pid={pid}
                lpTokenName={lpTokenName}
                earnTokenName={earnTokenName}
              />
            </EarnCard>
          </CardWrapper>
          <TopStakers
            lpTokenAddress={lpTokenAddress}
            icon={(stakeIcon ? stakeIcon : icon) + ''}
            name={name}
          ></TopStakers>
          <RewardsText>
            <span role="img" aria-label="star" style={{ marginRight: 10 }}>
              ⭐️
            </span>
            Every time you stake and unstake LP tokens, the contract will automatically harvest {otherName || earnTokenName} rewards
            for you!
          </RewardsText>
        </>
      )}
    </>
  )
}
